import {Button, openDeleteDialog, openDialog, showNotification} from 'platform/components';
import {Heading, HStack, Show, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useState} from 'react';
import {useParams} from 'react-router-dom';

import {head, isEmpty, isNil, mergeAll} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {
  useDeleteSelectableMaterialItemMutation,
  usePairSelectableMaterialItemMutation,
  useUnpairSelectableMaterialItemMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {
  catchUnhandledDataGridActions,
  DmsDataGrid,
  handleApiError,
  logError,
  refreshDmsDatagrid,
} from '@dms/shared';

import {useBoolean} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

import {SELECTABLE_MATERIAL_ITEM_DETAIL} from '../constants/SelectableMaterialItemDetail';
import {WarehouseArticleData} from '../types/WarehouseArticleData';
import {PairWarehouseArticleModal} from './PairWarehouseArticleModal';
import {SelectableMaterialItemDetail} from './SelectableMaterialItemDetail';

interface SelectableMaterialsDetailExternalSundriesProps {
  integrationId: string;
}

export function SelectableMaterialsDetailExternalSundries(
  props: SelectableMaterialsDetailExternalSundriesProps
) {
  const {id: selectableMaterialId} = useParams();

  const [isPairingModalVisible, openPairingModal, closePairingModal] = useBoolean(false);

  const [selectedItems, setSelectedItems] = useState<string[] | null>(null);

  const [pairItems] = usePairSelectableMaterialItemMutation({
    fixedCacheKey: 'pair-items-mutation',
  });

  const [unpairItems] = useUnpairSelectableMaterialItemMutation();

  const [deleteItems] = useDeleteSelectableMaterialItemMutation();

  const handlePairWarehouseArticle = async (warehouseArticleData: WarehouseArticleData) => {
    if (isNil(selectableMaterialId)) {
      return logError('selectableMaterialId is not defined');
    }

    if (isNil(selectedItems) || isEmpty(selectedItems)) {
      return logError('no items selected');
    }

    return await pairItems({
      selectableMaterialId,
      body: {
        selectableMaterialItemIds: selectedItems,
        warehouseArticleId: warehouseArticleData.articleId,
      },
    })
      .unwrap()
      .then(() => refreshDmsDatagrid('selectable-materials-external-sundries', {deselectAll: true}))
      .then(() =>
        showNotification.success(i18n.t('entity.settings.notifications.successfullyPaired'))
      )
      .catch(handleApiError);
  };

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = async ({
    actionKey,
    rowId,
    getColumnData,
    refreshData,
    deselectAll,
  }) => {
    await match(actionKey)
      .with('redirectDetail', () => {
        const selectableMaterialItemId = isArray(rowId) ? head(rowId) : rowId;

        if (isNil(selectableMaterialId)) {
          return logError('selectableMaterialId is not defined');
        }

        // We are not checking if the warehouseId is defined because the warehouseId is not required to open the item detail
        const warehouseId = getColumnData<{key: string}>('warehouse')?.value?.key;

        openDialog(
          <SelectableMaterialItemDetail
            selectableMaterialId={selectableMaterialId}
            selectableMaterialItemId={selectableMaterialItemId}
            integrationId={props.integrationId}
            warehouseId={warehouseId}
          />,
          {
            id: SELECTABLE_MATERIAL_ITEM_DETAIL,
          }
        );
      })
      .with('pair', () => {
        const ids = isArray(rowId) ? rowId : [rowId];
        setSelectedItems(ids);
        openPairingModal();
      })
      .with('unpair', async () => {
        const ids = isArray(rowId) ? rowId : [rowId];

        if (isNil(selectableMaterialId)) {
          return logError('selectableMaterialId is not defined');
        }

        await unpairItems({selectableMaterialId, body: {selectableMaterialItemIds: ids}})
          .unwrap()
          .then(deselectAll)
          .then(() => refreshData())
          .then(() =>
            showNotification.success(i18n.t('entity.settings.notifications.successfullyUnpaired'))
          )
          .catch(handleApiError);
      })
      .with('delete', () => {
        const ids = isArray(rowId) ? rowId : [rowId];

        if (isNil(selectableMaterialId)) {
          return logError('selectableMaterialId is not defined');
        }

        openDeleteDialog({
          onConfirm: () =>
            deleteItems({selectableMaterialId, body: {selectableMaterialItemIds: ids}})
              .unwrap()
              .then(deselectAll)
              .then(() => refreshData())
              .then(() =>
                showNotification.success(i18n.t('general.notifications.successfullyDeleted'))
              )
              .catch(handleApiError),
        });
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const queryModifier = (filter: QueryFilterObject) =>
    mergeAll([filter, {integrationId: props.integrationId}]);

  return (
    <>
      <Show when={isPairingModalVisible}>
        <PairWarehouseArticleModal
          selectableMaterialId={selectableMaterialId as string}
          onDiscard={closePairingModal}
          onPairWarehouseArticle={handlePairWarehouseArticle}
        />
      </Show>

      <VStack spacing={6} height="100%">
        <HStack justify="space-between">
          <Heading size={4}>{i18n.t('entity.settings.labels.externalSundries')}</Heading>
          <Button
            title={i18n.t('entity.settings.actions.addNewItem')}
            leftIcon="content/add_circle"
            variant="secondary"
            onClick={() =>
              openDialog(
                <SelectableMaterialItemDetail
                  selectableMaterialId={selectableMaterialId as string}
                  integrationId={props.integrationId}
                />,
                {
                  id: SELECTABLE_MATERIAL_ITEM_DETAIL,
                  title: i18n.t('entity.settings.actions.addNewItem'),
                }
              )
            }
          />
        </HStack>
        <DmsDataGrid
          gridCode="selectable-materials-external-sundries"
          predefinedColumns={PREDEFINED_COLUMNS}
          actionCallback={actionCallback}
          queryModifier={queryModifier}
          autoHeight
        />
      </VStack>
    </>
  );
}

const PREDEFINED_COLUMNS = ['warehouse'] as const;
